import { Component, inject, Input, OnInit } from '@angular/core'
import Performer from '@account/Performer'
import School from '@account/School'
import ClassCategory from '@account/ClassCategory'
import HttpError from '@ui/HttpError'
import { PerformerService } from '@account/Services/PerformerService'
import { CardComponent } from '@ui/Components/Card/Card.Component'
import { CardContentComponent } from '@ui/Components/Card/CardContent.Component'
import { Table } from '@ui/Table'
import { CategoryService } from '@account/Services/CategoryService'
import { CdkTableComponent } from '@ui/Components/Table/CdkTable.Component'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { SchoolListDialogComponent } from '@account/Dialogs/School/SchoolListDialog.Component'
import { ConfirmDeleteDialogComponent } from '@account/Dialogs/ConfirmDeleteDialog.Component'

interface CategorySchool extends ClassCategory {
	school: School | undefined
}

@Component({
	selector: 'performer-schools-component',
	templateUrl: './PerformerSchools.Component.html',
	imports: [CardComponent, CardContentComponent, CdkTableComponent],
})
export class PerformerSchoolsComponent
	extends Table<CategorySchool>
	implements OnInit
{
	@Input() public performer: Performer

	@Input() private readonly schoolUpdated: (
		performer: Performer,
	) => void | Promise<void>

	private readonly performerService: PerformerService

	private readonly categoryService: CategoryService

	private entities: CategorySchool[] = []

	private schoolListDialogComponentMatDialogRef: MatDialogRef<SchoolListDialogComponent>

	private confirmDeleteDialogComponentMatDialogRef: MatDialogRef<ConfirmDeleteDialogComponent>

	private readonly dialog: MatDialog

	constructor() {
		super()

		this.performerService = inject(PerformerService)
		this.categoryService = inject(CategoryService)
		this.dialog = inject(MatDialog)

		this.displayColumns = ['category', 'school', 'change', 'reset']

		this.categoryService.clearLocalStorageKey().resetFilter()
	}

	public ngOnInit() {
		this.tableEntities = [
			{
				columnDef: 'category',
				accessorKey: 'name',
				header: 'Category',
			},
			{
				columnDef: 'school',
				accessorCallback(entity: CategorySchool): string {
					return entity.school ? entity.school.name : 'No school selected'
				},
				header: 'School',
			},
			{
				columnDef: 'change',
				header: 'Change',
				type: 'action',
				action: {
					title: 'Change',
					color: 'primary',
					output: (entity: CategorySchool) => {
						this.schoolListDialogComponentMatDialogRef = this.dialog.open(
							SchoolListDialogComponent,
						)

						this.schoolListDialogComponentMatDialogRef.componentInstance.category =
							entity

						const selectedSchool = this.performer.schools.find(
							(school: School): boolean => {
								return school.pivot.class_category_id === entity.id
							},
						)

						this.schoolListDialogComponentMatDialogRef.componentInstance.selectedSchoolId =
							selectedSchool ? selectedSchool.id : undefined

						this.schoolListDialogComponentMatDialogRef
							.afterClosed()
							.subscribe(async (school: School) => {
								if (school) {
									await this.selectSchoolOnCat(school, entity)
								}
							})
					},
				},
			},
			{
				columnDef: 'reset',
				header: 'Reset',
				type: 'action',
				action: {
					title: 'Reset',
					color: 'warn',
					output: (entity: CategorySchool) => {
						this.clearSchoolFromCat(entity)
					},
				},
			},
		]

		this.fetchCategories()
	}

	public selectSchoolOnCat = async (
		school: School,
		category: ClassCategory,
	): Promise<void> => {
		if (school) {
			school.pivot = {
				school_id: school.id,
				class_category_id: category.id,
				performer_id: this.performer.id,
			}
		}

		let added = false

		this.performer.schools = this.performer.schools.map(
			(schoolItem: School): School => {
				if (schoolItem.pivot.class_category_id === category.id && school) {
					added = true

					return school
				}

				return schoolItem
			},
		)

		if (school && !added) {
			this.performer.schools.push(school)
		}

		try {
			this.performer = await this.performerService.update(this.performer)

			await this.fetchCategories()
		} catch (error) {
			if (error instanceof HttpError) {
				this.schoolUpdated(this.performer)
			} else {
				throw error
			}
		}
	}

	private readonly clearSchoolFromCat = async (
		category: ClassCategory,
	): Promise<void> => {
		this.confirmDeleteDialogComponentMatDialogRef = this.dialog.open(
			ConfirmDeleteDialogComponent,
			{
				disableClose: true,
			},
		)

		this.confirmDeleteDialogComponentMatDialogRef
			.afterClosed()
			.subscribe(async (confirm: boolean) => {
				if (confirm) {
					this.performer.schools = this.performer.schools.filter(
						(schoolItem: School): boolean => {
							return schoolItem.pivot.class_category_id !== category.id
						},
					)

					try {
						this.performer = await this.performerService.update(this.performer)

						await this.fetchCategories()
					} catch (error) {
						if (error instanceof HttpError) {
							this.schoolUpdated(this.performer)
						} else {
							throw error
						}
					}
				}
			})
	}

	private readonly fetchCategories = async (): Promise<void> => {
		this.categoryService.paginator.per_page = 999

		try {
			const categories = await this.categoryService.filter()

			this.entities = categories.map((category: ClassCategory) => {
				const school = this.performer.schools.find(
					(school: School): boolean => {
						return school.pivot.class_category_id === category.id
					},
				)

				return {
					...category,
					school,
				} as CategorySchool
			})

			this.setTableData(this.entities)
		} catch (error) {
			if (!(error instanceof HttpError)) {
				throw error
			}
		}
	}
}
